import {
  InfiniteData,
  QueryClient,
  QueryKey,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";

import { organizationUsersKey } from "src/api/taktile/queries";
import { organizationsApi, organizationUsersApi } from "src/api/taktileApi";
import {
  OrganizationUser,
  UserOrganizationInviteData,
} from "src/clients/taktile-api";
import {
  OrganizationUserUpdatePayload,
  OrganizationUserWorkspacesRolesUpdatePayload,
} from "src/store/api/types";

type OrgUsersListCacheEntry = InfiniteData<OrganizationUser[]>;
type PreviousUsersSnapshot = [QueryKey, OrgUsersListCacheEntry | undefined][];

const buildOnMutate =
  (queryClient: QueryClient, orgId: string, userId: string) =>
  (
    newUser:
      | OrganizationUserUpdatePayload
      | OrganizationUserWorkspacesRolesUpdatePayload,
  ) => {
    const queryKey = organizationUsersKey.list(orgId);
    const previousUsersSnapshot =
      queryClient.getQueriesData<OrgUsersListCacheEntry>({ queryKey });

    const updateUser = (user: OrganizationUser) =>
      user.id === userId ? { ...user, ...newUser } : user;

    queryClient.setQueriesData<OrgUsersListCacheEntry>(
      { queryKey },
      (previous) =>
        previous && {
          pages: previous.pages.map((page) => page.map(updateUser)),
          pageParams: previous.pageParams,
        },
    );

    queryClient.invalidateQueries({
      queryKey,
      refetchType: "none",
    });

    return { previousUsers: previousUsersSnapshot };
  };

const buildOnError =
  (queryClient: QueryClient) =>
  (
    _err: unknown,
    _newUser: unknown,
    context?: { previousUsers?: PreviousUsersSnapshot },
  ) => {
    if (context?.previousUsers) {
      for (const [key, data] of context.previousUsers) {
        queryClient.setQueryData(key, data);
      }
    }
  };

export const useUpdateOrganizationUser = (orgId: string, userId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: OrganizationUserUpdatePayload) =>
      (
        await organizationUsersApi.updateUserApiV1OrganizationsOrgIdUsersUserIdPatch(
          orgId,
          userId,
          data,
        )
      ).data,
    onMutate: buildOnMutate(queryClient, orgId, userId),
    onError: buildOnError(queryClient),
  });
};

export const useUpdateOrganizationUserWorkspacesRoles = (
  orgId: string,
  userId: string,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: OrganizationUserWorkspacesRolesUpdatePayload) =>
      (
        await organizationUsersApi.updateUserWsRolesApiV1OrganizationsOrgIdUsersUserIdWsRolesPatch(
          orgId,
          userId,
          data,
        )
      ).data,
    onMutate: buildOnMutate(queryClient, orgId, userId),
    onError: buildOnError(queryClient),
  });
};

export const useDeleteOrganizationInvite = (
  orgId: string,
  inviteId: string,
) => {
  const queryClient = useQueryClient();
  const isNotCurrentlyInvitedUser = (user: OrganizationUser) =>
    user.invite_id !== inviteId;

  return useMutation({
    mutationFn: () =>
      organizationsApi.deleteOrganizationInviteApiV1OrganizationsOrganizationIdInvitesInviteIdDelete(
        orgId,
        inviteId,
      ),
    onMutate: () => {
      const queryKey = organizationUsersKey.list(orgId);
      const previousUsers = queryClient.getQueriesData<OrgUsersListCacheEntry>({
        queryKey,
      });

      queryClient.setQueriesData<OrgUsersListCacheEntry>(
        { queryKey },
        (previous) =>
          previous && {
            pages: previous.pages.map((page) =>
              page.filter(isNotCurrentlyInvitedUser),
            ),
            pageParams: previous.pageParams,
          },
      );

      queryClient.invalidateQueries({
        queryKey,
        refetchType: "none",
      });

      return { previousUsers };
    },
    onError: buildOnError(queryClient),
  });
};

export const useCreateOrganizationInvite = (orgId: string) => {
  const queryClient = useQueryClient();
  const queryKey = organizationUsersKey.list(orgId);

  return useMutation({
    mutationFn: (payload: UserOrganizationInviteData) => {
      return organizationsApi.postOrganizationInviteApiV1OrganizationsOrganizationIdInvitesPost(
        orgId,
        payload,
      );
    },
    onSuccess: ({ data }) => {
      queryClient.setQueriesData<OrgUsersListCacheEntry>(
        { queryKey },
        (previous) =>
          previous && {
            pages: [
              ...(previous.pages ?? []),
              [{ ...data.user, invite_id: data.id }],
            ],
            pageParams: previous.pageParams,
          },
      );

      queryClient.invalidateQueries({
        queryKey,
        refetchType: "none",
      });
    },
    onMutate: () => ({
      previousUsers: queryClient.getQueriesData<OrgUsersListCacheEntry>({
        queryKey,
      }),
    }),
    onError: buildOnError(queryClient),
  });
};
